/* IndexInfoModal — разказва на потребителя какво означава даден индекс
 * (DIL, Nutri-Score, NOVA, NRF, GL, PRAL, Ω6:3, FODMAP, Satiety, DII, Endo)
 * и кога е полезен. Използва централизираната библиотека INDEX_INFO.
 *
 * Usage:
 *   <IndexInfoButton indexKey="DIL" />   // icon button + modal
 *   <IndexInfoModal index={INDEX_INFO.DIL} open={open} onClose={...} />
 */
import React, { useState, useMemo } from "react";
import { Info, X } from "lucide-react";
import { INDEX_INFO } from "@/data/foodIndices";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";

/* Keywords we look for inside a condition line to match it against the user's
 * declared medical conditions. Keys are normalised (lowercase, Bulgarian +
 * English). Map → канонично име → [keywords to match in bullet text, all lowercase].
 */
const CONDITION_KEYWORDS = {
  insulin_resistance: ["инсулин", "insulin", "ir ", "t2d", "т2д", "hba1c"],
  pcos: ["pcos", "пкос", "rotterdam"],
  diabetes: ["диабет", "diabetes", "t1d", "t2d", "hba1c"],
  hashimoto: ["hashimoto", "хашимото", "tpo-ab", "thyroid", "хипотир"],
  graves: ["graves", "grav", "хипертир"],
  endometriosis: ["ендометр", "endometr", "endo score"],
  obesity: ["обезитет", "obesity", "weight loss", "затлъст"],
  ckd: ["ckd", "бъбре", "хбн", "kdigo", "nephro"],
  kidney_stones: ["камъни в бъбреци", "kidney stones", "uric acid"],
  osteoporosis: ["остеопороза", "osteoporos", "osteopen"],
  hypertension: ["хипертония", "hypertension"],
  anemia: ["анемия", "anemia", "iron"],
  ibs: ["ibs"],
  sibo: ["sibo"],
  ibd: ["ibd", "crohn", "улцероз", "uc "],
  gluten: ["celiac", "целиакия", "gluten", "глутен", "ncgs"],
  cvd: ["cvd", "ccз", "ссз", "cardio", "mace", "кардио"],
  breast_cancer: ["breast cancer", "рак на гърдата", "tnbc", "luminal", "her2", "pam50"],
  alzheimer: ["alzheimer", "dementia", "деменция", "когнитив"],
  depression: ["депресия", "depression", "anxiety", "тревожност"],
  autoimmune: ["автоимун", "autoimmune", "rheumato", "ra ", "lupus", "лупус"],
  pregnancy: ["пренатал", "pregnancy", "бременн", "lactation", "кърмен"],
  elderly: ["възрастни", "elderly", "sarcopen", "frail"],
};

/* For a given user condition string (free-form like "PCOS", "T2 Diabetes", etc),
 * return the canonical keyword group it belongs to, or null. */
function normaliseUserCondition(raw) {
  const s = (raw || "").toLowerCase().trim();
  if (!s) return null;
  for (const [canonical, keywords] of Object.entries(CONDITION_KEYWORDS)) {
    if (keywords.some((k) => s.includes(k))) return canonical;
  }
  // Also treat the raw string itself as a loose keyword for direct matching below.
  return s;
}

/* Returns true if a bullet text references any of the user's canonical
 * condition tags. */
function bulletMatchesUser(bulletText, userTags) {
  if (!userTags?.length) return false;
  const text = (bulletText || "").toLowerCase();
  for (const tag of userTags) {
    const keywords = CONDITION_KEYWORDS[tag];
    if (keywords && keywords.some((k) => text.includes(k))) return true;
    // Fall through: raw substring match for unknown user condition words
    if (tag.length > 3 && text.includes(tag)) return true;
  }
  return false;
}

function ModalBody({ index, isDark, bg }) {
  const ranges = index.ranges_bg || [];
  const conditions = bg ? (index.conditions_bg || []) : (index.conditions_en || []);
  const { user } = useAuth();

  // Derive user's normalised condition tags once per modal open.
  const userTags = useMemo(() => {
    const rawList = user?.profile?.conditions || user?.profile?.medical_conditions || [];
    return rawList.map(normaliseUserCondition).filter(Boolean);
  }, [user]);

  // Partition + sort conditions: matched ones first (in original order),
  // then rest. The user sees their own diagnoses highlighted at the top.
  const sortedConditions = useMemo(() => {
    if (!userTags.length) return conditions.map((c) => ({ text: c, matched: false }));
    const matched = [];
    const rest = [];
    for (const c of conditions) {
      if (bulletMatchesUser(c, userTags)) matched.push({ text: c, matched: true });
      else rest.push({ text: c, matched: false });
    }
    return [...matched, ...rest];
  }, [conditions, userTags]);

  const hasMatches = sortedConditions.some((c) => c.matched);

  return (
    <>
      <div className={`text-[11px] italic leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
        {bg ? index.short_bg : index.short_en}
      </div>

      <div>
        <div className={`text-[10px] uppercase tracking-[0.12em] font-semibold mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {bg ? "Формула" : "Formula"}
        </div>
        <code className={`block text-[11px] p-2 rounded break-words ${isDark ? "bg-white/5 text-sky-300" : "bg-gray-50 text-sky-700"}`}>
          {index.formula}
        </code>
      </div>

      {ranges.length > 0 && (
        <div>
          <div className={`text-[10px] uppercase tracking-[0.12em] font-semibold mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg ? "Диапазони" : "Ranges"}
          </div>
          <div className="space-y-1">
            {ranges.map((r) => (
              <div key={r.label} className="flex items-center gap-2 text-[11px]">
                <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: r.color }} />
                <span className={`font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>{r.label}</span>
                <span className={`${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  [{r.min} – {r.max === 999 ? "∞" : r.max}]
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className={`text-[10px] uppercase tracking-[0.12em] font-semibold mb-1 flex items-center gap-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          <span>{bg ? "При кои заболявания е полезен" : "Conditions where it's useful"}</span>
          {hasMatches && (
            <span
              className={`px-1.5 py-0.5 rounded text-[9px] font-semibold normal-case tracking-normal ${isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}
              data-testid="my-profile-filter-badge"
            >
              {bg ? "подредено по моя профил" : "sorted by my profile"}
            </span>
          )}
        </div>
        <ul className="space-y-1.5">
          {sortedConditions.map((c, i) => (
            <li
              key={`${index.key}-c-${i}`}
              data-testid={c.matched ? `condition-matched-${i}` : `condition-bullet-${i}`}
              className={`text-[11px] leading-relaxed rounded px-2 py-1 transition-colors
                ${c.matched
                  ? (isDark ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-100"
                            : "bg-emerald-50 border border-emerald-200 text-emerald-900")
                  : (isDark ? "text-gray-300" : "text-gray-700")}`}
            >
              {c.matched && (
                <span className={`inline-block text-[9px] uppercase font-semibold tracking-wider mr-1.5 px-1 py-0.5 rounded ${isDark ? "bg-emerald-500/30 text-emerald-200" : "bg-emerald-500 text-white"}`}>
                  {bg ? "моят профил" : "my profile"}
                </span>
              )}
              {c.text.split(/\*\*(.+?)\*\*/).map((part, idx) =>
                idx % 2 === 1
                  ? <span key={`b-${idx}-${String(part).slice(0, 16)}`} className={`font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{part}</span>
                  : <span key={`t-${idx}-${String(part).slice(0, 16)}`}>{part}</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className={`pt-2 border-t text-[10px] ${isDark ? "border-white/10 text-gray-500" : "border-gray-100 text-gray-500"}`}>
        <div><span className="font-semibold">{bg ? "Източник: " : "Source: "}</span>{index.citation}</div>
        <div className="mt-0.5"><span className="font-semibold">{bg ? "Лицензиране: " : "License: "}</span>{index.license}</div>
      </div>
    </>
  );
}

export default function IndexInfoModal({ indexKey, open, onClose }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  if (!open) return null;

  const index = INDEX_INFO[indexKey];
  if (!index) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid={`index-info-modal-${indexKey}`}
    >
      <div
        className={`w-full sm:max-w-md max-h-[90vh] flex flex-col rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden
          ${isDark ? "bg-[hsl(222,47%,10%)] text-gray-100" : "bg-white text-gray-900"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`shrink-0 px-4 py-3 flex items-center justify-between border-b
          ${isDark ? "border-white/10" : "border-gray-100"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Info className="w-4 h-4 text-sky-500 shrink-0" />
            <div className="text-sm font-semibold truncate">
              {bg ? index.name_bg : index.name_en}
            </div>
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100 shrink-0" data-testid="index-info-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
          <ModalBody index={index} isDark={isDark} bg={bg} />
        </div>
      </div>
    </div>
  );
}

/* Convenience trigger — малък бутон-иконка, отваря модала. */
export function IndexInfoButton({ indexKey, className = "" }) {
  const [open, setOpen] = useState(false);
  const { isDark } = useTheme();
  if (!INDEX_INFO[indexKey]) return null;
  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center justify-center w-4 h-4 rounded-full transition-colors
          ${isDark ? "text-gray-500 hover:text-sky-400 hover:bg-sky-500/10" : "text-gray-400 hover:text-sky-600 hover:bg-sky-50"} ${className}`}
        data-testid={`index-info-btn-${indexKey}`}
        aria-label={`Info about ${indexKey}`}
        title={indexKey}
      >
        <Info className="w-3 h-3" />
      </button>
      <IndexInfoModal indexKey={indexKey} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
